import type { ReactNode } from 'react';
import { Columns, type Column } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { VolumeDay } from '../types';
import { usePicked } from './picking';
import { SeeTheNumbers } from './SeeTheNumbers';
import { daySpan, type RenderBreakdown, type Span } from './spans';
import {
  bucketsOf,
  comparedFrom,
  type Bucket,
  type Buckets,
  type Earlier,
  type Range,
} from './usage-range';
import type { LinkFocus } from './link';
import { compact, dayWord, isoWeekday, peakIndex, trailingMean } from './volume-math';
import { UsageCard } from './UsageCard';
import './insights.css';

function bucketTitle(b: Bucket, locale: string): string {
  return b.first === b.last
    ? daySpan(b.first, locale).title
    : `Week of ${dayWord(b.first, locale)} to ${dayWord(b.last, locale, true)}`;
}

/** The exact earlier value for the tooltip: "732 on Jun 23, 13 weeks earlier". */
function earlierLine(
  e: Earlier | undefined,
  weeks: number,
  f: Formatter,
  locale: string,
): string[] {
  if (!e) return [];
  const when =
    e.first === e.last ? dayWord(e.last, locale) : `the week to ${dayWord(e.last, locale)}`;
  return [`${f.int(e.pages)} on ${when}, ${weeks} weeks earlier`];
}

function columnsOf(b: Buckets, f: Formatter, locale: string): Column[] {
  const buckets = b.now;
  const peak = peakIndex(buckets.map((x) => x.pages));
  return buckets.map((x, i) => ({
    axis: dayWord(x.last, locale),
    value: x.pages,
    mark: i === peak ? f.int(x.pages) : undefined,
    tip: {
      value: `${f.int(x.pages)} pages`,
      title: bucketTitle(x, locale),
      lines: [`${f.int(x.documents)} documents`, ...earlierLine(b.earlier[i], b.weeks, f, locale)],
      hint: 'Click, or drag across, for the jobs and intake behind it',
    },
  }));
}

/** Columns dim when a linked chart points at a weekday or a month they are not in. */
function dimmer(buckets: readonly Bucket[], focus: LinkFocus) {
  if (!focus || focus.kind === 'day') return undefined;
  if (focus.kind === 'weekday')
    return (i: number) =>
      buckets[i]!.first !== buckets[i]!.last || isoWeekday(buckets[i]!.first) !== focus.weekday;
  return (i: number) =>
    !buckets[i]!.last.startsWith(focus.month) && !buckets[i]!.first.startsWith(focus.month);
}

/** Where the ghost line starts, said once (round 2, dataviz N1): nothing is drawn before it. */
function compareNote(b: Buckets, locale: string): string | null {
  const from = comparedFrom(b);
  if (from === 0) return null;
  if (from < 0) return 'Nothing earlier to compare with yet';
  return `Before ${dayWord(b.now[from]!.first, locale)} there is nothing to compare`;
}

function Legend({ daily, b, locale }: { daily: boolean; b: Buckets; locale: string }): ReactNode {
  const note = compareNote(b, locale);
  return (
    <ul className="ad-legend" aria-hidden="true">
      <li>
        <span className="ad-legend__swatch ad-legend__swatch--bar" />
        Pages {daily ? 'a day' : 'a week'}
      </li>
      {daily ? (
        <li>
          <span className="ad-legend__swatch ad-legend__swatch--line" />
          7-day average
        </li>
      ) : null}
      {comparedFrom(b) >= 0 ? (
        <li>
          <span className="ad-legend__swatch ad-legend__swatch--ghost" />
          {daily ? 'Same weekdays' : 'Same weeks'}, {b.weeks} weeks earlier
          {daily ? ' (7-day average)' : ''}
        </li>
      ) : null}
      {note ? <li>{note}</li> : null}
    </ul>
  );
}

/** The span a picked run of columns stands for: its first day to its last. */
function bucketSpan(now: readonly Bucket[], from: number, to: number, locale: string): Span {
  const a = now[from]!;
  const b = now[to]!;
  return {
    from: a.first,
    to: b.last,
    title:
      from === to
        ? bucketTitle(a, locale)
        : `${dayWord(a.first, locale)} to ${dayWord(b.last, locale, true)}`,
  };
}

function numbersOf(now: readonly Bucket[], f: Formatter, locale: string) {
  return [...now].reverse().map((b) => ({
    key: b.last,
    cells: [bucketTitle(b, locale), f.int(b.pages), f.int(b.documents)],
    values: [b.last, b.pages, b.documents],
  }));
}

const NUMBER_COLUMNS = [
  { heading: 'Day or week' },
  { heading: 'Pages', numeric: true },
  { heading: 'Documents', numeric: true },
];

/** The first day with pages: the earlier of the API's first page (a UTC day) and the first day read with pages. */
function historyFrom(days: readonly VolumeDay[], firstPagesAt: string | null): string | null {
  const read = days.find((d) => d.pages > 0)?.day;
  const first = firstPagesAt?.slice(0, 10);
  if (!read || !first) return read ?? first ?? null;
  return read < first ? read : first;
}

export interface DailyPagesCardProps {
  days: readonly VolumeDay[];
  range: Range;
  timeZone: string;
  locale: string;
  /** When the company's first pages came in (ISO), so the ghost is not drawn over no history. */
  firstPagesAt: string | null;
  focus: LinkFocus;
  onFocus: (focus: LinkFocus) => void;
  renderBreakdown?: RenderBreakdown | undefined;
}

/**
 * Pages over the page's range: a column a day for 30 or 90 days with the 7-day average, a column a
 * week for 12 or 24 months; the same weekdays whole weeks earlier as a ghost line. A click or a drag
 * opens the detail behind a day, a week or a span of them; the column under the pointer lights its
 * weekday and month in the charts below.
 */
export function DailyPagesCard(props: DailyPagesCardProps): ReactNode {
  const { range, locale } = props;
  const f = useFormat();
  const b = bucketsOf(props.days, range, historyFrom(props.days, props.firstPagesAt));
  const now = b.now;
  const daily = range.endsWith('d');
  const pick = usePicked(props.renderBreakdown, (from, to) => bucketSpan(now, from, to, locale));
  const total = now.reduce((s, b) => s + b.pages, 0);
  return (
    <UsageCard
      id="ad-daily"
      title={daily ? 'Pages a day' : 'Pages a week'}
      empty={total === 0 ? 'No pages in this range.' : null}
      note={`In their time (${props.timeZone}). Click a column, or drag across several, for what is behind it.`}
    >
      <Legend daily={daily} b={b} locale={locale} />
      <Columns
        columns={columnsOf(b, f, locale)}
        ghost={b.ghost}
        format={compact}
        selected={pick.picked}
        line={daily ? trailingMean(props.days.map((d) => d.pages)).slice(-now.length) : undefined}
        label={`Pages ${daily ? 'a day' : 'a week'} over the range: ${f.int(total)} in all.`}
        dim={dimmer(now, props.focus)}
        onSelect={pick.onSelect}
        onSpan={pick.onSpan}
        onActive={(i) => props.onFocus(i === null ? null : { kind: 'day', day: now[i]!.last })}
      />
      {pick.detail}
      <SeeTheNumbers
        caption="Pages and documents over the range"
        columns={NUMBER_COLUMNS}
        rows={numbersOf(now, f, locale)}
      />
    </UsageCard>
  );
}
